import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Skeleton,
  Stack,
  Switch as ToggleSwitch,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { Delete, Edit } from '@barracuda-internal/bds-core/dist/Icons/Core';

import AADRoleGuard from 'common/components/AADRoleGuard';
import { actions } from './globalReducer';

const EMPTY_FORM = { name: '', default_url: '', default_timeout: '30', enabled: true, apiKey: '', clientId: '', clientSecret: '' };

/**
 * Global backend definitions — the platform-wide catalog of backend
 * services (default URL, timeout, enabled flag). Per-account overrides
 * live on the account detail page, not here.
 *
 * ADMIN role: create, edit, delete a backend definition.
 * OPS / SUPPORT: read-only.
 */
const BackendsContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const backendDefinitions = useSelector((state) => state.global.backendDefinitions);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBackend, setEditingBackend] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(actions.fetchBackendDefinitions());
  }, [dispatch]);

  const items = backendDefinitions?.items || [];
  const isLoading = backendDefinitions?.isLoading;
  const error = backendDefinitions?.error;

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingBackend(null);
    setForm(EMPTY_FORM);
  };

  const openCreateDialog = () => {
    setEditingBackend(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (backend) => {
    setEditingBackend(backend);
    setForm({
      name: backend.name || '',
      default_url: backend.default_url || '',
      default_timeout: (backend.default_timeout ?? 30).toString(),
      enabled: backend.enabled !== false,
      apiKey: '',
      clientId: '',
      clientSecret: '',
    });
    setDialogOpen(true);
  };

  const submit = () => {
    const payload = {
      name: form.name.trim(),
      default_url: form.default_url.trim(),
      default_timeout: parseFloat(form.default_timeout) || 30,
      enabled: form.enabled,
    };
    if (form.apiKey) payload.api_key = form.apiKey;
    if (form.clientId) payload.client_id = form.clientId;
    if (form.clientSecret) payload.client_secret = form.clientSecret;

    if (editingBackend) {
      // Backend expects only mutable fields on PATCH; name is not editable.
      const { name, ...updatable } = payload;
      dispatch(actions.updateBackendDefinition(editingBackend.id, updatable));
    } else {
      dispatch(actions.createBackendDefinition(payload));
    }
    closeDialog();
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(actions.deleteBackendDefinition(deleteTarget.id));
      setDeleteTarget(null);
    }
  };

  const isCreate = !editingBackend;
  const canSubmit =
    (isCreate ? form.name.trim() : true) && form.default_url.trim();

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: tokens.aliasesSpaceComponentDefault,
          }}
        >
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
            <Typography variant="h2">Backend services</Typography>
            <Typography variant="captionSubtle" sx={{ maxWidth: 600 }}>
              Platform-wide backend service definitions — the default URL and
              timeout for each backend Gateway can talk to. Per-account
              overrides are managed from the individual account page.
            </Typography>
          </Stack>
          <AADRoleGuard requires="ADMIN">
            <Button bdsType="interactiveEmphasis" onClick={openCreateDialog}>
              Add backend
            </Button>
          </AADRoleGuard>
        </Stack>

        <Card bdsType="staticSubtle" bdsBoxShadow="default" bdsPadding="spacious">
          <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
            <Typography variant="h4">Definitions</Typography>

            {isLoading ? (
              <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                <Skeleton variant="rectangular" width="100%" height={60} />
                <Skeleton variant="rectangular" width="100%" height={60} />
                <Skeleton variant="rectangular" width="100%" height={60} />
              </Stack>
            ) : error ? (
              <Typography variant="body1" sx={{ color: tokens.aliasesColorTextError }}>
                {error}
              </Typography>
            ) : items.length === 0 ? (
              <Box
                sx={{
                  padding: tokens.aliasesSpaceContainerSpacious,
                  textAlign: 'center',
                  border: `1px dashed ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                  borderRadius: tokens.aliasesBorderRadiusDefault,
                }}
              >
                <Typography variant="body1">
                  No backend definitions configured
                </Typography>
              </Box>
            ) : (
              <List>
                {items.map((backend) => (
                  <ListItem
                    key={backend.id}
                    bdsType="staticSubtle"
                    padding="default"
                    sx={{
                      borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: tokens.aliasesSpaceComponentDefault,
                      }}
                    >
                      <Stack sx={{ gap: tokens.aliasesSpaceComponentDense, flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          sx={{ gap: tokens.aliasesSpaceComponentDense, alignItems: 'center' }}
                        >
                          <Typography variant="body1Bold">{backend.name}</Typography>
                          {backend.enabled ? (
                            <Chip label="Enabled" bdsType="staticSuccess" bdsSize="medium" />
                          ) : (
                            <Chip label="Disabled" bdsType="staticNeutral" bdsSize="medium" />
                          )}
                        </Stack>
                        <Typography variant="captionSubtle" sx={{ wordBreak: 'break-all' }}>
                          {backend.default_url || '—'}
                        </Typography>
                        <Typography variant="captionSubtle">
                          Timeout: {backend.default_timeout ?? '—'}s
                          {backend.has_api_key && ' · API key set'}
                          {backend.has_client_id && ' · Client ID set'}
                          {backend.has_client_secret && ' · Client secret set'}
                        </Typography>
                      </Stack>
                      <AADRoleGuard requires="ADMIN">
                        <Stack direction="row" sx={{ gap: tokens.aliasesSpaceComponentDense }}>
                          <IconButton
                            bdsType="interactiveSubtle"
                            size="small"
                            onClick={() => openEditDialog(backend)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            bdsType="interactiveSubtle"
                            size="small"
                            onClick={() => setDeleteTarget(backend)}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </AADRoleGuard>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} bdsDialogWidth="small">
        <DialogTitle
          dialogTitle={editingBackend ? `Edit ${editingBackend.name}` : 'Add backend definition'}
          showCloseIcon
          onClose={closeDialog}
        />
        <DialogContent>
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault, marginTop: tokens.aliasesSpaceComponentDense }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!!editingBackend}
              helperText={editingBackend ? 'Name cannot be changed after creation.' : 'e.g. dlp, llm_guard, intent_classifier'}
              fullWidth
              bdsSize="small"
            />
            <TextField
              label="Default URL"
              value={form.default_url}
              onChange={(e) => setForm({ ...form, default_url: e.target.value })}
              fullWidth
              bdsSize="small"
              placeholder="https://backend.example.com"
            />
            <TextField
              label="Default timeout (seconds)"
              type="number"
              value={form.default_timeout}
              onChange={(e) => setForm({ ...form, default_timeout: e.target.value })}
              fullWidth
              bdsSize="small"
            />
            <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDefault }}>
              <Typography variant="body1" sx={{ flex: 1 }}>Enabled</Typography>
              <ToggleSwitch
                checked={form.enabled}
                onChange={(_, checked) => setForm({ ...form, enabled: checked })}
              />
            </Stack>
            <Typography variant="body1Bold" sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
              Authentication
            </Typography>
            <TextField
              label={editingBackend ? 'API key (leave blank to keep existing)' : 'API key (optional)'}
              value={form.apiKey}
              onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
              type="password"
              fullWidth
              bdsSize="small"
            />
            <TextField
              label={editingBackend ? 'Client ID (leave blank to keep existing)' : 'Client ID (optional)'}
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              fullWidth
              bdsSize="small"
            />
            <TextField
              label={editingBackend ? 'Client secret (leave blank to keep existing)' : 'Client secret (optional)'}
              value={form.clientSecret}
              onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
              type="password"
              fullWidth
              bdsSize="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeDialog}>Cancel</Button>
          <Button
            bdsType="interactiveEmphasis"
            onClick={submit}
            disabled={!canSubmit}
          >
            {editingBackend ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        bdsDialogWidth="small"
      >
        <DialogTitle
          dialogTitle={`Delete ${deleteTarget?.name || 'backend'}`}
          showCloseIcon
          onClose={() => setDeleteTarget(null)}
        />
        <DialogContent
          content={
            deleteTarget
              ? `This will permanently delete the "${deleteTarget.name}" backend definition. Any account overrides for this backend will also stop working. This action cannot be undone.`
              : ''
          }
        />
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button bdsType="interactiveEmphasis" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BackendsContainer;
